import { useEffect, useMemo } from "react";
import { LoadingCircle } from "@allaria-tech/matter/LoadingCircle";

import Layout from "../Layout/Layout";
import PartnerConsentFeedback from "./PartnerConsentFeedback";
import PartnerConsentPreconfirmation from "./PartnerConsentPreconfirmation";
import PartnerConsentWelcome from "./PartnerConsentWelcome";
import { useAppDispatch } from "../../app/hooks";
import { onUpdateTheme } from "../../reducers/activeTemplateReducer";
import usePartnerConsent from "../../hooks/usePartnerConsent";
import usePartnershipRedirect from "../../hooks/usePartnershipRedirect";
import { getConsentCopy } from "../../i18n/partnerConsent";
import { PartnerConsentFlow, leaveToPartner } from "../../utils/partnerConsent";
import useWindowWidth from "../../hooks/useWindowWidth";

/** Matches the stylesheet's breakpoint and Layout's own mobile treatment. */
const MOBILE_BREAKPOINT = 768;

/**
 * Consent flow for people arriving from a partner's app. Which partner, and
 * whether the link opens or links an account, comes from config and the link
 * itself, so a new bank is a registry entry rather than a new screen.
 */
const PartnerConsentPage = ({ flow }: { flow: PartnerConsentFlow }) => {
  const dispatch = useAppDispatch();
  const isMobile = useWindowWidth().width < MOBILE_BREAKPOINT;
  const {
    screen,
    variant,
    cuit,
    comitentes,
    selectedComitente,
    selectComitente,
    name,
    lastName,
    holder,
    comitente,
    accepted,
    isSubmitting,
    toggleAccepted,
    submitWelcome,
    confirmPreconfirmation,
    backToWelcome,
    retry,
  } = usePartnerConsent(flow);

  const copy = useMemo(
    () =>
      getConsentCopy({
        variant,
        partner: flow.name,
        host: flow.host.name,
        supportEmail: flow.supportEmail,
      }),
    [variant, flow.name, flow.host.name, flow.supportEmail],
  );

  // Resolves `redirect_url` against the whitelist, then the configured
  // default. Undefined when neither survives: hide the message and button.
  const { redirect, secondsLeft } = usePartnershipRedirect({
    config: flow.successRedirect,
    enabled: screen === "SUCCESS",
    onElapsed: (url) => leaveToPartner(flow, url),
  });

  // No template interpreter here, so nothing else sets Layout's theme.
  useEffect(() => {
    dispatch(onUpdateTheme("ALLARIA"));
  }, [dispatch]);

  const { feedback } = copy;

  const renderScreen = () => {
    switch (screen) {
      case "PRECONFIRMATION":
        return (
          <PartnerConsentPreconfirmation
            flow={flow}
            copy={copy}
            cuit={cuit}
            firstName={name}
            lastName={lastName}
            comitente={selectedComitente}
            isMobile={isMobile}
            isSubmitting={isSubmitting}
            onBack={backToWelcome}
            onConfirm={confirmPreconfirmation}
          />
        );

      case "SUCCESS":
        return (
          <PartnerConsentFeedback
            flow={flow}
            isMobile={isMobile}
            type={feedback.success.type}
            title={feedback.success.title}
            description={feedback.success.description}
            countdown={
              redirect?.autoRedirectMs && redirect.message(secondsLeft)
            }
            buttonText={redirect?.buttonText}
            onButtonClick={() =>
              redirect && leaveToPartner(flow, redirect.url)
            }
          />
        );

      case "ALREADY_ACCEPTED":
        return (
          <PartnerConsentFeedback
            flow={flow}
            isMobile={isMobile}
            type="SUCCESS"
            title={feedback.alreadyAccepted.title}
            description={feedback.alreadyAccepted.description}
            // Signed and burned: nothing left to do or undo.
          />
        );

      case "COMPLETED":
        return (
          <PartnerConsentFeedback
            flow={flow}
            isMobile={isMobile}
            type="SUCCESS"
            title={feedback.completed.title}
            description={feedback.completed.description(
              copy.welcome.account(holder, comitente),
            )}
          />
        );

      case "INVALID_LINK":
        return (
          <PartnerConsentFeedback
            flow={flow}
            isMobile={isMobile}
            type="ERROR"
            title={feedback.invalidLink.title}
            description={feedback.invalidLink.description}
            // No button: a dead link has nowhere useful to send someone.
          />
        );

      case "ERROR":
        return (
          <PartnerConsentFeedback
            flow={flow}
            isMobile={isMobile}
            type="ERROR"
            title={feedback.genericError.title}
            description={feedback.genericError.description}
            buttonText={feedback.retry}
            onButtonClick={retry}
          />
        );

      default:
        return (
          <PartnerConsentWelcome
            flow={flow}
            copy={copy}
            variant={variant}
            name={name}
            comitentes={comitentes}
            selectedComitente={selectedComitente}
            onSelectComitente={selectComitente}
            holder={holder}
            comitente={comitente}
            accepted={accepted}
            isMobile={isMobile}
            isSubmitting={isSubmitting}
            onToggle={toggleAccepted}
            onSubmit={submitWelcome}
          />
        );
    }
  };

  // Nothing to put on the page yet, so the loader gets its own centred layout.
  if (screen === "LOADING")
    return (
      <Layout
        hideHeader
        hideMobileHeader
        hideStepper
        hideSupportButton
        isLoading
      >
        <LoadingCircle />
      </Layout>
    );

  // Support shows on the screens still being worked through, not the terminal
  // ones. The preconfirmation is the desktop exception: its action bar spans
  // the bottom, where the control would otherwise float.
  const showSupport =
    screen === "WELCOME" || (isMobile && screen === "PRECONFIRMATION");

  // Our own lockup heads every screen, so Layout's header stands down.
  return (
    <Layout
      hideHeader
      hideMobileHeader
      hideStepper
      hideSupportButton={!showSupport}
    >
      {renderScreen()}
    </Layout>
  );
};

export default PartnerConsentPage;
